import * as uuid from "uuid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { changeDocumentPartStructureAsync, checkDifferencesAsync } from "../../redux/feature/document/document-slice";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { WithApiFileLocationProvider } from "../document/WithApiFileLocationProvider";
import { fetchDocumentRoots } from "../../api/document/Location";
import { FileLocation } from "../document/FileLocationProvider";
import { DocumentLocationExplorer } from "../document/DocumentLocationExplorer";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { ExportDocumentPanel } from "../document/ExportDocumentPanel";
import { DocumentOutline } from "../document/DocumentOutline";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import { searchCleared, selectionCleared } from "../../redux/feature/cpm/outline-search-slice";
import {
  selectAzureDefaultSite,
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectSharePointHostName,
  selectUserFunctions,
  setIxbrlSettings,
  setSeahorseDocumentLinked,
} from "../../redux/feature/settings/configurations-slice";
import { CHANGE_DOC_STRUCTURE_NOTIFICATION } from "../../utils/constants/notification";
import { FindItem, useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { PageDocumentRoots } from "../../api/models/page-document-roots";
import { fetchDocumentPartDetails, fetchWorkflowStatusForRoots } from "../../api/document/DocumentsPart";
import { SearchBar } from "../generic/searchBar/SearchBar";
import { DBMapping } from "../document/DBMapping";
import { ChangeComponentSource } from "../document/ChangeComponentSource";
import { ChangeTemplate } from "../document/ChangeTemplate";
import { ManageStructure } from "../document/ManageStructure";
import { DeleteDocumentHelper } from "../../utils/helpers/DeleteDocumentsHelper";
import { fetchIxbrlSettings, fetchSeahorseDocumentLinked } from "../../pages/seahorse/SeahorseUtils";
import { store } from "../../redux/store/store";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { DocumentPart } from "../../api/models";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { EmbeddedRoots } from "../../api/models/embedded-roots";
import { AlignDocumentPanel } from "../document/AlignDocumentPanel";
import { TgkDocumentElaborationRequest } from "../../api/models/tgk-document-elaboration-request";

interface DocumentListWrapperState {
  loaded: boolean;
  selectedTreeItem: FlatPathTreeItem | undefined;
  currentOperationItemId: string | undefined;
  page:
    | "folders"
    | "outline"
    | "export"
    | "dbmapping"
    | "managestructure"
    | "align"
    | "changecomponentsource"
    | "changetemplate";
  treeItems: any;
  modifiedFields: boolean;
  deleteItem: DocumentPart;
  alignItem: DocumentPart;
}

const documentListWrapperInitialState: DocumentListWrapperState = {
  loaded: true,
  selectedTreeItem: undefined,
  currentOperationItemId: undefined,
  page: "folders",
  treeItems: [],
  modifiedFields: false,
  deleteItem: null,
  alignItem: null,
};

export interface DocumentListProps {
  idPartRoot?: string;
}

export const findBlockingElaboration = (item: FindItem) =>
  item.elaborationType !== "SHEET_AND_AREA" && elaborationStatusItemIsBlocking(item);

const SITE = "/sites/";

export const DocumentsList: React.FunctionComponent<DocumentListProps> = (props) => {
  const [state, setState] = React.useState<DocumentListWrapperState>(documentListWrapperInitialState);
  const [pagination, setPagination] = React.useState<PageDocumentRoots>();
  const username = useAppSelector(selectCpmUsername);
  const backendUrl = useAppSelector(selectBackendUrl);
  const clientId = useAppSelector(selectClientId);
  const azureDefaultSite = useAppSelector(selectAzureDefaultSite);
  const sharePointHostName = useAppSelector(selectSharePointHostName);

  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState(null);
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const notAuthorizedUserForIXBRL =
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions) &&
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);
  const [documentPartWithStatus, setDocumentPartWithStatus] = useState<DocumentPartWithStatus[] | null>(null);
  const [isCertifiedOrApprovedOrRejected, setIsCertifiedOrApprovedOrRejected] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (searchQuery || searchQuery === "") {
      setState({
        ...state,
        loaded: false,
        page: "folders",
      });
    }
  }, [searchQuery]);

  useEffect(() => {
    dispatch(searchCleared());
    dispatch(selectionCleared());
  }, [dispatch, state.selectedTreeItem?.document?.id]);

  function fetchAndSetWorkflowStatuses(embeddedRoots: EmbeddedRoots) {
    const myRequestId = ++requestIdRef.current;
    setDocumentPartWithStatus(null);
    const ids = embeddedRoots?._embedded?.content?.map((df) => df.documentPart.id).filter(Boolean) ?? [];
    if (ids.length > 0) {
      fetchWorkflowStatusForRoots(ids)
        .then((result) => {
          if (myRequestId === requestIdRef.current) setDocumentPartWithStatus(result);
        })
        .catch(() => {
          if (myRequestId === requestIdRef.current) setDocumentPartWithStatus([]);
        });
    } else {
      setDocumentPartWithStatus([]);
    }
  }

  async function onSelectItem(item: FlatPathTreeItem | undefined) {
    if (!item?.document || item.document.folder) setState({ ...state, selectedTreeItem: undefined });
    else {
      // Navigating to the outline, which now owns documentPartWithStatus (it accumulates the
      // per-part statuses of the opened document). Invalidate any in-flight library roots fetch
      // so its roots-only result can't overwrite those statuses and leave child parts stuck on
      // the loading spinner.
      requestIdRef.current++;
      if (item.document.root) {
        item.document = await fetchDocumentPartDetails(item.document.id);
      }
      setState({ ...state, selectedTreeItem: item, page: "outline" });
    }
  }

  let timer;
  const showOutline =
    state.selectedTreeItem?.document && !state.selectedTreeItem.document.folder && state.page === "outline";

  function handleChangeSearch(query: string) {
    clearTimeout(timer);

    timer = setTimeout(() => {
      setSearchQuery(query);
    }, 1000);
  }

  function updateTreeItems(treeItems: any, modifiedFields: boolean) {
    setState({ ...state, treeItems, modifiedFields });
  }

  const changeDocumentPartStructure = async () => {
    await changeDocumentPartStructureAsync({
      channelIdToNotify: props.idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      username,
      structureDocumentPart: state.treeItems,
      documentId: state.selectedTreeItem.document.id,
      dispatch,
      backendUrl,
      clientId,
    });
    setState({
      ...state,
      loaded: false,
      modifiedFields: false,
    });
  };

  const changePage = (
    page: "dbmapping" | "managestructure" | "outline" | "changecomponentsource" | "changetemplate"
  ) => {
    setState((s) => ({
      ...s,
      page: page,
    }));
  };

  const goToOutline = async () => {
    setState((s) => ({
      ...s,
      page: "outline",
      loaded: false,
    }));
  };

  const blockingElaboration = useFilteredElaborations({ findFunction: findBlockingElaboration });

  // Memoized so the provider can safely depend on it (a fresh getItems triggers a reload) without refetch loops.
  const getItems = useCallback(async () => {
    if (!notAuthorizedUserForIXBRL) {
      fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
      fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
    }
    const page = "0";
    const embeddedRoots = await fetchDocumentRoots(searchQuery, page);
    setState((s) => ({ ...s, loaded: true }));
    setPagination(embeddedRoots.page);
    // Skip when the outline is shown: it owns documentPartWithStatus (root + child parts),
    // and overwriting it with roots-only data would leave child parts stuck loading.
    if (!showOutline) {
      fetchAndSetWorkflowStatuses(embeddedRoots);
    }
    return embeddedRoots?._embedded?.content?.map<FileLocation>((df) => {
      return {
        key: df.documentPart.driveItemId,
        text: df.documentPart.name.replace(/\.docx$/, ""),
        iconName: "WordLogoInverse",
        document: df.documentPart,
        readPermission: df.hasReadPermissions,
      };
    });
  }, [searchQuery, notAuthorizedUserForIXBRL, showOutline]);

  if (state.page === "export") {
    return (
      <ExportDocumentPanel
        documentId={state.currentOperationItemId}
        onCloseModal={goToOutline}
        channelIdToNotify={props.idPartRoot}
      />
    );
  } else if (state.page === "dbmapping") {
    return (
      <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
        <DBMapping selectedTreeItem={state.selectedTreeItem} onBackButtonClick={goToOutline} />
      </ElaborationWrapper>
    );
  } else if (state.page === "changecomponentsource") {
    return (
      <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
        {/* IDS-7053/IDS-7054: rootDocumentId must be the outline root id - the same
            `selectedTreeItem.document.id` expression DocumentOutline uses to compute its own `rootId` - and never
            the id of a document part selected further down the tree, since component instances are looked up per
            root document only. */}
        <ChangeComponentSource
          selectedTreeItem={state.selectedTreeItem}
          rootDocumentId={state.selectedTreeItem.document.id}
          onBackButtonClick={goToOutline}
        />
      </ElaborationWrapper>
    );
  } else if (state.page === "changetemplate") {
    return (
      <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
        {/* Same rootDocumentId convention as changecomponentsource above: the outline root id, not a
            deeper-selected document part id, since the template change applies to the root document. */}
        <ChangeTemplate
          selectedTreeItem={state.selectedTreeItem}
          rootDocumentId={state.selectedTreeItem.document.id}
          channelIdToNotify={props.idPartRoot}
          onBackButtonClick={goToOutline}
        />
      </ElaborationWrapper>
    );
  } else if (state.page === "managestructure") {
    return (
      <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
        <ManageStructure
          changeDocumentPartStructure={changeDocumentPartStructure}
          selectedTreeItem={state.selectedTreeItem}
          updateTreeItems={updateTreeItems}
          modifiedFields={state.modifiedFields}
          onBackButtonClick={goToOutline}
          elaborationItem={blockingElaboration}
        />
      </ElaborationWrapper>
    );
  } else if (state.page === "align") {
    return (
      <AlignDocumentPanel
        documentPartToAlign={state.alignItem}
        onCloseModal={goToOutline}
        channelIdToNotify={props.idPartRoot}
      />
    );
  }

  const onCheckDifferencesDocument = async (item: FlatPathTreeItem) => {
    const tgkDocumentElaborationRequest: TgkDocumentElaborationRequest = {
      correlationId: uuid.v4(),
      clientId: store.getState().configurationState.clientId,
      userId: username,
      channelIdToNotify: props.idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      documentId: item.key,
      documentName: item.text,
      isOpenDocument: false,
      isAlignNeeded: true,
      typeElaborationRequest: "tgkDocumentElaborationRequest",
    };

    await checkDifferencesAsync(tgkDocumentElaborationRequest);
  };

  return (
    <div
      style={{
        marginBottom: "-2.5rem",
        ...(showOutline
          ? {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }
          : {}),
      }}
    >
      <div style={showOutline ? { flexShrink: 0 } : undefined}>
        <HeaderPanel applyPadding>
          {showOutline
            ? formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
                documentName: state.selectedTreeItem.document.name.replace(/\.docx$/, ""),
              })
            : formatMessage("cof.opendocuments.library")}
        </HeaderPanel>
      </div>
      {!showOutline && <SearchBar onSearch={handleChangeSearch}></SearchBar>}
      <div
        className="ms-Grid"
        dir="ltr"
        style={
          showOutline
            ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }
            : undefined
        }
      >
        <WithApiFileLocationProvider
          selectDepth="ITEM"
          cleanState={!state.loaded}
          getItems={getItems}
          pagination={pagination}
        >
          {!showOutline && (
            <DocumentLocationExplorer
              onSelectItem={onSelectItem}
              page={"OPEN"}
              searchQuery={searchQuery}
              showDownloadButton={true}
              documentPartWithStatus={documentPartWithStatus}
              setDocumentPartWithStatus={setDocumentPartWithStatus}
            />
          )}
        </WithApiFileLocationProvider>

        {showOutline && (
          <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                paddingBottom: "110px",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <DocumentOutline
                isLibraryRibbon={true}
                isOpenOutlineRibbon={true}
                enableFilter={true}
                selectedTreeItem={state.selectedTreeItem}
                idPartRoot={props.idPartRoot}
                onDeleteDocument={(item) => setState((s) => ({ ...s, deleteItem: (item as any).data }))}
                onExportDocument={(item) =>
                  setState((s) => ({
                    ...s,
                    currentOperationItemId: item.key,
                    page: "export",
                  }))
                }
                onAlignDocument={(item) => setState((s) => ({ ...s, alignItem: (item as any).data, page: "align" }))}
                onCheckDifferencesDocument={onCheckDifferencesDocument}
                updateTreeItems={updateTreeItems}
                changePage={changePage}
                documentPartWithStatus={documentPartWithStatus}
                setDocumentPartWithStatus={setDocumentPartWithStatus}
                blockingElaboration={blockingElaboration}
                siteId={state.selectedTreeItem.document.siteId}
                driveItemId={state.selectedTreeItem.document.driveItemId}
                setIsCertifiedOrApprovedOrRejected={setIsCertifiedOrApprovedOrRejected}
                isCertifiedOrApprovedOrRejected={isCertifiedOrApprovedOrRejected}
              />
            </div>
          </ElaborationWrapper>
        )}

        <DeleteDocumentHelper
          partRoot={state.selectedTreeItem?.document}
          onCancelDelete={() => setState((s) => ({ ...s, deleteItem: null, loaded: false, page: "folders" }))}
          deleteItem={state.deleteItem}
        />

        <FooterPanel>
          <>
            {showOutline && (
              <PrimaryButton
                text={formatMessage("cof.opendocuments.library")}
                onClick={() => {
                  setSearchQuery("");
                  setDocumentPartWithStatus(null);
                  dispatch(searchCleared());
                  dispatch(selectionCleared());
                  setState((s) => ({
                    ...s,
                    page: "folders",
                    loaded: false,
                  }));
                }}
                disabled={!showOutline && !state.selectedTreeItem}
                style={{
                  float: "left",
                }}
                className={ButtonVariants.white}
              />
            )}
            {!showOutline && (
              <a
                style={{ float: "right" }}
                href={
                  "https://" +
                  sharePointHostName +
                  (!azureDefaultSite?.includes("sites/") && !azureDefaultSite?.includes("teams/") ? SITE : "") +
                  azureDefaultSite
                }
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage("cof.opendocuments.openinsharepoint")}
              </a>
            )}
          </>
        </FooterPanel>
      </div>
    </div>
  );
};
